
import { Options, Vue } from 'vue-class-component';
import { Prop, Ref } from 'vue-property-decorator';
import NIcon from '../icons/NIcon.vue';
import NInputEye from './NInputEye.vue';

export enum NInputSize {
  Big = 'big',
  Normal = 'normal'
}

@Options({
  components: { NIcon, NInputEye },
  emits: ['update:modelValue', 'focus', 'blur']
})
export default class NInput extends Vue {
  @Prop({ type: [String, Number], required: false })
  readonly modelValue?: string | number;

  @Prop({ type: String })
  readonly placeholder?: string;

  @Prop({ type: String, default: NInputSize.Normal })
  readonly size!: NInputSize;

  @Prop({ type: Boolean, default: false })
  readonly readonly!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly password!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly plain!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly autofocus!: boolean;

  @Prop({ type: String })
  readonly accesskey?: string;

  @Prop({ type: String })
  readonly suffixIcon?: string;

  @Prop({ type: Boolean, default: false })
  readonly clearable!: boolean;

  @Prop({ type: String })
  readonly dataQa?: string;

  @Prop({ type: String, default: '' })
  readonly alphabet!: string;

  @Prop({ type: Number, default: Infinity })
  readonly maxLength!: number;

  @Ref()
  readonly input!: HTMLInputElement;

  private isPasswordVisible = false;

  get type() {
    return this.password && !this.isPasswordVisible ? 'password' : 'text';
  }

  get model() {
    return this.modelValue ?? '';
  }

  set model(value) {
    const result = this.filterValueByMask(value as string) ?? '';
    this.input.value = result;
    this.$emit('update:modelValue', result);
  }

  inputHandler(e: any) {
    this.model = e?.target?.value;
  }

  clear() {
    this.input.value = '';
    this.$emit('update:modelValue', '');
    this.$emit('change', '');
  }

  get classes() {
    const root = 'n-input';
    return {
      [root]: true,
      [`${root}__input`]: true,
      [`${root}__input_plain`]: this.plain,
      [`${root}__input_suffix`]: !!this.suffixIcon,
      [`${root}__input_postfix`]: this.clearable,
      [`${root}__input_password`]: this.password,
      [`${root}__size_${this.size}`]: this.size
    };
  }

  private filterValueByMask(value?: string) {
    const canFilter = this.alphabet && value?.length;
    if (!canFilter) return value;

    const allowed = new Set(this.alphabet);
    const result = [...value!].filter((v) => allowed.has(v)).join('');
    return result;
  }

  private dispatchChangeEvent(): void {
    const result = this.filterValueByMask(this.input.value);
    this.$emit('change', result);
  }

  private eventProxyHandler(e: Event) {
    this.$emit(e.type, e);
  }

  focus(): void {
    this.input.focus();
  }
}
